import { HttpErrorResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { CoreActions } from '@next/core-lib';
import { DataSerie, DataSerieItem, DataSeriesParameter } from '@next/core-lib/models';
import { NotificationActions } from '@next/core-lib/notification';
import { UserSelectors } from '@next/core-lib/user';
import { ofType, Actions, createEffect } from '@ngrx/effects';
import { Action, Store } from '@ngrx/store';
import { Observable, of } from 'rxjs';
import {
  catchError,
  switchMap,
  exhaustMap,
  startWith,
  finalize,
  withLatestFrom,
} from 'rxjs/operators';

import * as cycleActions from '../../../cycle/store/actions/cycle.actions';
import {
  DataViewType,
  FlockDataParameter,
  FLOCK_DATA_FILTERS,
  NormalizedFlockDataObject as NormalizedFlockDataObject,
  TimeSeriesResponse,
} from '../../models/flock-report-service.model';
import { ReportModuleState } from '../../models/report.model';
import { ReportService } from '../../services/flock-report.service';

import * as reportActions from '../actions/report.actions';

import { getSelectedDataViewType, getSelectedReport } from '../selectors/report.selectors';

@Injectable()
export class ReportEffects {
  private readonly actions$ = inject(Actions);
  private readonly apiReport = inject(ReportService);
  private readonly store = inject<Store<ReportModuleState>>(Store);

  /** Inserted by Angular inject() migration for backwards compatibility */
  constructor(...args: unknown[]);

  constructor() {}

  loadData$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(reportActions.loadData),
      switchMap(() => [
        cycleActions.clearCycleState(),
        reportActions.clearReportState(),
        cycleActions.loadCyclesRequest(),
      ]),
    ),
  );

  loadReport$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(reportActions.loadReport),
      switchMap((request) => [
        reportActions.clearSelectedReport(),
        reportActions.setNormalizedFlockDataObject({
          normalizedFlockDataObject: null,
        }),
        reportActions.loadTimeSeriesRequest({
          getFlockDataRequest: {
            barnIds: request.barnId ? [request.barnId] : [],
            dataViewType: request.barnId ? DataViewType.PerCycleBarn : DataViewType.PerCycle,
            conceptId: request.conceptId,
            parameters: FLOCK_DATA_FILTERS,
            locationIds: [],
            cycleIds: [request.cycleId],
          },
        }),
        reportActions.setSelectedReport({
          reportSelection: {
            cycleId: request.cycleId,
            barnId: request.barnId,
          },
        }),
      ]),
    ),
  );

  loadTimeSeries$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(reportActions.loadTimeSeriesRequest),
      exhaustMap((getFlockDataRequest) =>
        this.apiReport.getFlockData(getFlockDataRequest.getFlockDataRequest).pipe(
          switchMap((flockDataResponse: TimeSeriesResponse) =>
            of(
              reportActions.loadTimeSeriesSuccess({ timeSeries: flockDataResponse.data }),
              reportActions.convertToNormalizedFlockDataObject({
                timeSeries: flockDataResponse.data,
              }),
            ),
          ),
          catchError((error: HttpErrorResponse) => {
            if (error.status === 404) {
              return of(
                reportActions.loadTimeSeriesSuccess({ timeSeries: null }),
                reportActions.setNormalizedFlockDataObject({
                  normalizedFlockDataObject: null,
                }),
              );
            }
            this.store.dispatch(CoreActions.setLoading({ loading: false }));
            return of(
              reportActions.loadTimeSeriesFailed({ error: error.message }),
              NotificationActions.errorSnackbar({ message: error.message }),
            );
          }),
          startWith(CoreActions.setLoading({ loading: true })),
          finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
        ),
      ),
    ),
  );

  convertToNormalizedFlock$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(reportActions.convertToNormalizedFlockDataObject),
      withLatestFrom(
        this.store.select(getSelectedDataViewType),
        this.store.select(UserSelectors.selectLocations),
        this.store.select(getSelectedReport),
      ),
      exhaustMap(([{ timeSeries }, dataview, locations, reportSelection]) => {
        let normalizedFlockDataObject: NormalizedFlockDataObject = null;
        if (timeSeries) {
          Object.assign([], timeSeries.metaData.parameters)
            .sort((a: DataSeriesParameter, b: DataSeriesParameter) =>
              <number>(<unknown>a.properties['Order']) > <number>(<unknown>b.properties['Order'])
                ? 1
                : -1,
            )
            .forEach((param: DataSeriesParameter) => {
              const timeSeriesDataArray = timeSeries.series[param.code];
              let timeSeriesData: DataSerie<number, Date> = null;
              if (dataview === DataViewType.PerCycle) {
                timeSeriesData = timeSeriesDataArray.find(
                  (x) => (x.properties['CycleId'] as unknown as number) === reportSelection.cycleId,
                );
              } else {
                timeSeriesData = timeSeriesDataArray.find(
                  (x) =>
                    (x.properties['CycleId'] as unknown as number) === reportSelection.cycleId &&
                    (x.properties['BarnId'] as unknown as number) === reportSelection.barnId,
                );
              }

              if (!timeSeriesData) {
                return;
              }

              if (normalizedFlockDataObject === null) {
                normalizedFlockDataObject = {
                  fromDate: undefined,
                  toDate: undefined,
                  breeds: undefined,
                };
                if (timeSeriesData.properties['LocationId']) {
                  normalizedFlockDataObject.locationId = <number>(
                    (<unknown>timeSeriesData.properties['LocationId'])
                  );
                  const correspondingLocation = locations.find(
                    (d) => d.id === normalizedFlockDataObject.locationId,
                  );
                  if (correspondingLocation) {
                    normalizedFlockDataObject.locationName = correspondingLocation.name;
                  }
                }
                if (timeSeriesData.properties['CycleId']) {
                  normalizedFlockDataObject.cycleId = <number>(
                    (<unknown>timeSeriesData.properties['CycleId'])
                  );
                  normalizedFlockDataObject.cycleNumber = <number>(
                    (<unknown>timeSeriesData.properties['CycleNumber'])
                  );
                }
                if (timeSeriesData.properties['BarnId']) {
                  normalizedFlockDataObject.barnId = <number>(
                    (<unknown>timeSeriesData.properties['BarnId'])
                  );
                  normalizedFlockDataObject.barnName = <string>(
                    (<unknown>timeSeriesData.properties['BarnName'])
                  );
                }
                if (timeSeriesData.properties['Breeds']) {
                  normalizedFlockDataObject.breeds = <string>(
                    (<unknown>timeSeriesData.properties['Breeds'])
                  );
                }
                if (timeSeriesData.properties['Breed']) {
                  normalizedFlockDataObject.breeds = <string>(
                    (<unknown>timeSeriesData.properties['Breed'])
                  );
                }
              }

              if (timeSeriesData.values.length > 0) {
                const metaDataParameter = timeSeries.metaData.parameters.find(
                  (d) => d.code === timeSeriesData.parameterCode,
                );

                const tsDataItem = timeSeriesData.values[0];

                normalizedFlockDataObject = this.assignValue(
                  metaDataParameter,
                  normalizedFlockDataObject,
                  tsDataItem,
                );
              }
            });
        }
        return of(
          reportActions.setNormalizedFlockDataObject({
            normalizedFlockDataObject,
          }),
        );
      }),
    ),
  );

  private assignValue(
    metaDataParameter: DataSeriesParameter,
    normalizedFlock: NormalizedFlockDataObject,
    tsDataItem: DataSerieItem<number, Date>,
  ): NormalizedFlockDataObject {
    switch (metaDataParameter.name) {
      case FlockDataParameter.DayOldChicks:
        normalizedFlock.dayOldChicks = tsDataItem.value;
        break;
      case FlockDataParameter.DayOldChicksPrice:
        normalizedFlock.dayOldChicksPrice = tsDataItem.value;
        break;
      case FlockDataParameter.DayOldChicksPriceExclDiscounts:
        normalizedFlock.dayOldChicksPriceExclDiscounts = tsDataItem.value;
        break;
      case FlockDataParameter.TotalDeparture:
        normalizedFlock.totalDeparture = tsDataItem.value;
        break;
      case FlockDataParameter.DepartureWeight:
        normalizedFlock.departureWeight = tsDataItem.value;
        break;
      case FlockDataParameter.SlaughterWeight:
        normalizedFlock.slaughterWeight = tsDataItem.value;
        break;
      case FlockDataParameter.Revenue:
        normalizedFlock.revenue = tsDataItem.value;
        break;
      case FlockDataParameter.RevenueExclDiscounts:
        normalizedFlock.revenueExclDiscounts = tsDataItem.value;
        break;
      case FlockDataParameter.FeedBalance:
        normalizedFlock.feedBalance = tsDataItem.value;
        break;
      case FlockDataParameter.FeedBalancePrice:
        normalizedFlock.feedBalancePrice = tsDataItem.value;
        break;
      case FlockDataParameter.FeedBalancePriceExclDiscounts:
        normalizedFlock.feedBalancePriceExclDiscounts = tsDataItem.value;
        break;
      case FlockDataParameter.LossPercentage:
        normalizedFlock.lossPercentage = tsDataItem.value;
        break;
      case FlockDataParameter.ReplacementPercentage:
        normalizedFlock.replacementPercentage = tsDataItem.value;
        break;
      case FlockDataParameter.FeedConversion:
        normalizedFlock.feedConversion = tsDataItem.value;
        break;
      case FlockDataParameter.FeedConversion1500:
        normalizedFlock.feedConversion1500 = tsDataItem.value;
        break;
      case FlockDataParameter.AverageWeightInGram:
        normalizedFlock.averageWeightInGram = tsDataItem.value;
        break;
      case FlockDataParameter.WeightedAverageAgeInDays:
        normalizedFlock.weightedAverageAgeInDays = tsDataItem.value;
        break;
      case FlockDataParameter.WeightedAverageGrowthPerDay:
        normalizedFlock.weightedAverageGrowthPerDay = tsDataItem.value;
        break;
      case FlockDataParameter.ProductionNumber:
        normalizedFlock.productionNumber = tsDataItem.value;
        break;
      case FlockDataParameter.DayOldChicksPerSquareMeter:
        normalizedFlock.dayOldChicksPerSquareMeter = tsDataItem.value;
        break;
      case FlockDataParameter.FeedUsageInKgPerSquareMeter:
        normalizedFlock.feedUsageInKgPerSquareMeter = tsDataItem.value;
        break;
      case FlockDataParameter.FeedConsumptionPricePer100Kg:
        normalizedFlock.feedConsumptionPricePer100Kg = tsDataItem.value;
        break;
      case FlockDataParameter.PaidAmountPerKg:
        normalizedFlock.paidAmountPerKg = tsDataItem.value;
        break;
      case FlockDataParameter.NettoRevenue:
        normalizedFlock.nettoRevenue = tsDataItem.value;
        break;
      case FlockDataParameter.NettoRevenuePerChick:
        normalizedFlock.nettoRevenuePerChick = tsDataItem.value;
        break;
      case FlockDataParameter.NettoRevenuePerSquareMeter:
        normalizedFlock.nettoRevenuePerSquareMeter = tsDataItem.value;
        break;
      case FlockDataParameter.NettoRevenuePerSquareMeterPerDay:
        normalizedFlock.nettoRevenuePerSquareMeterPerDay = tsDataItem.value;
        break;
      case FlockDataParameter.RejectionRate:
        normalizedFlock.rejectionRate = tsDataItem.value;
        break;
      case FlockDataParameter.DeliveredKgPerSquareMeter:
        normalizedFlock.deliveredKgPerSquareMeter = tsDataItem.value;
        break;
      case FlockDataParameter.NettoProfitInclOtherCosts:
        normalizedFlock.nettoProfitInclOtherCosts = tsDataItem.value;
        break;

      default:
        break;
    }
    return normalizedFlock;
  }
}
